import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, CalendarOff } from "lucide-react";
import { toast } from "react-hot-toast";
import { calendarService } from "../../../services/calendarService";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay();

const MonthCalendar = ({ year, month, blockedMap, onDateClick }) => {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const cells = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="border border-canopy-border rounded-xl p-4 bg-white">
      <h3 className="text-sm font-bold text-canopy-text mb-3 text-center">
        {MONTH_NAMES[month]}
      </h3>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div
            key={d}
            className="text-xs font-semibold text-canopy-textMuted py-1"
          >
            {d}
          </div>
        ))}
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} className="py-1" />;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isBlocked = blockedMap.has(dateStr);
          const label = blockedMap.get(dateStr);
          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => onDateClick(dateStr, isBlocked)}
              title={
                isBlocked
                  ? label || "Non-workable day — click to remove"
                  : "Click to mark as non-workable"
              }
              className={`text-xs rounded py-1 transition-colors font-medium ${
                isBlocked
                  ? "bg-red-500 text-white hover:bg-red-700"
                  : "text-canopy-text hover:bg-red-50 hover:text-red-600"
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const CalendarOverview = ({ userPermission }) => {
  const [blockedDays, setBlockedDays] = useState([]);
  const [pendingDate, setPendingDate] = useState(null);
  const [labelInput, setLabelInput] = useState("");
  const [saving, setSaving] = useState(false);
  const labelRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear + 1];

  useEffect(() => {
    calendarService
      .getNonworkableDays()
      .then(setBlockedDays)
      .catch(() => toast.error("Failed to load non-workable days"));
  }, []);

  useEffect(() => {
    if (pendingDate) labelRef.current?.focus();
  }, [pendingDate]);

  const blockedMap = useMemo(
    () => new Map(blockedDays.map((d) => [d.date, d.label ?? null])),
    [blockedDays],
  );

  const handleDelete = async (dateStr) => {
    try {
      await calendarService.deleteNonworkableDay(dateStr);
      setBlockedDays((prev) => prev.filter((d) => d.date !== dateStr));
      toast.success("Date removed");
    } catch {
      toast.error("Failed to remove date");
    }
  };

  const handleDateClick = (dateStr, isBlocked) => {
    if (isBlocked) {
      handleDelete(dateStr);
    } else {
      setPendingDate(dateStr);
      setLabelInput("");
    }
  };

  const handleSave = async () => {
    if (!pendingDate) return;
    setSaving(true);
    try {
      const created = await calendarService.createNonworkableDay(
        pendingDate,
        labelInput.trim() || null,
      );
      setBlockedDays((prev) => [
        ...prev,
        { id: created.id, date: created.date, label: created.label },
      ]);
      setPendingDate(null);
      setLabelInput("");
      toast.success("Date marked as non-workable");
    } catch (err) {
      toast.error(err.message || "Failed to save date");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelPending = () => {
    setPendingDate(null);
    setLabelInput("");
  };

  const sortedBlocked = useMemo(
    () => [...blockedDays].sort((a, b) => a.date.localeCompare(b.date)),
    [blockedDays],
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Floating modal */}
      {pendingDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl border border-canopy-border p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-canopy-text">
                Mark as Non-Workable
              </h3>
              <button
                type="button"
                onClick={handleCancelPending}
                className="text-canopy-textMuted hover:text-canopy-text transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-canopy-textMuted mb-4">
              Date:{" "}
              <span className="font-semibold text-canopy-text">
                {pendingDate}
              </span>
            </p>
            <div>
              <label className="block text-sm font-semibold text-canopy-text mb-2">
                Festivity / Reason
              </label>
              <input
                ref={labelRef}
                type="text"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") handleCancelPending();
                }}
                placeholder="e.g. New Year's Day"
                className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                type="button"
                onClick={handleCancelPending}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-canopy-border hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="canopy-btn-primary"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary header */}
      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <CalendarOff className="h-5 w-5 text-red-500" />
          <div>
            <h2 className="text-4xl font-bold text-canopy-text">
              Non-Workable Days
            </h2>
            <p className="text-sm text-canopy-textMuted mt-1">
              Click any date to mark it as non-workable. Click a red date to
              remove it.
            </p>
          </div>
        </div>

        {sortedBlocked.length === 0 ? (
          <p className="text-sm text-canopy-textMuted italic">
            No non-workable days registered.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {sortedBlocked.map((d) => (
              <div
                key={d.date}
                className="inline-flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-200 rounded-full text-xs font-semibold text-red-700"
              >
                {d.date}
                {d.label && <span className="text-red-500">— {d.label}</span>}
                <button
                  type="button"
                  onClick={() => handleDelete(d.date)}
                  className="text-red-400 hover:text-red-700 transition ml-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Year grids */}
      {years.map((year) => (
        <section
          key={year}
          className="bg-white border border-canopy-border rounded-xl p-6"
        >
          <h2 className="text-4xl font-bold text-canopy-text mb-6">{year}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 12 }, (_, month) => (
              <MonthCalendar
                key={month}
                year={year}
                month={month}
                blockedMap={blockedMap}
                onDateClick={handleDateClick}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default CalendarOverview;
